import React, { useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ChevronLeft, Download, Upload, FileText, CheckCircle } from "lucide-react";
import AdminLayout from "../../layout/admin";
import GenericTable from "../../components/custom/GenericTable";
import { getMethodApiCall, postMethodApiCall } from "../../services/api";
import { downloadFile } from "../../utils/helpers";

const IndustryApproval = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const {
    traineeSummary = [],
    month,
    year,
    monthName,
    industryId,
  } = location.state || {};

  const [consentChecked, setConsentChecked] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [hasDownloaded, setHasDownloaded] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [signedSealConfirmed, setSignedSealConfirmed] = useState(false);

  const eligibleStudents = traineeSummary.filter(
    (t) => t.attendancePercentage >= 80
  );
  const selectedTraineeIds = eligibleStudents
    .map((t) => t?.trainee_id)
    .filter((id) => Number.isInteger(id));

  const columns = [
    {
      header: "Registration ID",
      render: (row) => row?.registration_id || "-",
    },
    {
      header: "Trainee Name",
      render: (row) => row?.name || "-",
    },
    {
      header: "ITI",
      className: "text-center",
      cellClassName: "text-center",
      render: (row) => row?.iti_name || "-",
    },
    {
      header: "Trade",
      className: "text-center",
      cellClassName: "text-center",
      render: (row) => row?.trade_name || "-",
    },
    {
      header: "Total Working Days",
      className: "text-center",
      cellClassName: "text-center",
      render: (row) => row?.totalWorkingDays ?? "-",
    },
    {
      header: "Total Days Present",
      className: "text-center",
      cellClassName: "text-center",
      render: (row) => row?.daysPresent ?? "-",
    },
    {
      header: "Attendance %",
      className: "text-center",
      cellClassName: "text-center",
      render: (row) => {
        const pct = row?.attendancePercentage;
        if (pct == null) return "-";
        return <span className="font-semibold text-green-600">{pct}%</span>;
      },
    },
  ];

  const paginationConfig = {
    page: 1,
    limit: eligibleStudents.length || 10,
    totalRecords: eligibleStudents.length,
    totalPages: 1,
    onPageChange: () => {},
    onLimitChange: () => {},
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const itiId = eligibleStudents[0]?.iti_id;
      const tradeId = eligibleStudents[0]?.trade_id;
      if (!itiId || !tradeId) {
        alert("Missing ITI or trade information. Please go back and try again.");
        return;
      }

      const traineeIdsQuery = selectedTraineeIds.join(",");
      const url = `/v1/api/industry/attendance-by-industry/${industryId}/download-pdf?year=${year}&month=${month}&iti_id=${itiId}&trade_id=${tradeId}&traineeIds=${encodeURIComponent(traineeIdsQuery)}`;
      const res = await getMethodApiCall(url, {}, { responseType: "blob" });
      if (res?.status === 200 && res?.data) {
        downloadFile({
          data: res.data,
          mimeType: "application/pdf",
          filePrefix: `DST_Attendance_${monthName}_${year}`,
          extension: "pdf",
        });
        setHasDownloaded(true);
      } else {
        console.error("Download failed with status:", res?.status);
      }
    } catch (error) {
      console.error("Error downloading excel:", error);
    } finally {
      setDownloading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.type !== "application/pdf") {
      alert("Only PDF files are allowed.");
      e.target.value = "";
      return;
    }

    setUploadedFile(file);
    setSignedSealConfirmed(false);
  };

  const handleSubmitApproval = async () => {
    setSubmitting(true);
    try {
      const payload = {
        year,
        month,
        trainees: eligibleStudents.map((t) => ({
          trainee_id: t.trainee_id,
          iti_id: t.iti_id,
          trade_id: t.trade_id,
          totalWorkingDays: t.totalWorkingDays,
          daysPresent: t.daysPresent,
          attendancePercentage: t.attendancePercentage,
        })),
      };

      const res = await postMethodApiCall(
        `/v1/api/industry/attendance-by-industry/${industryId}/submit-approval`,
        payload
      );

      if (res?.status === 200 && res?.data?.success) {
        setSubmitted(true);
      } else {
        alert(res?.data?.message || "Failed to submit approval. Please try again.");
      }
    } catch (error) {
      console.error("Error submitting approval:", error);
      alert("Failed to submit approval. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleFinalSubmit = async () => {
    if (!uploadedFile || !industryId || eligibleStudents.length === 0) return;

    const itiId = eligibleStudents[0]?.iti_id;
    const tradeId = eligibleStudents[0]?.trade_id;
    if (!itiId || !tradeId) {
      alert("Missing ITI or trade information. Please go back and try again.");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("signed_copy", uploadedFile);
      formData.append("year", year);
      formData.append("month", month);
      formData.append("iti_id", String(itiId));
      formData.append("trade_id", String(tradeId));
      formData.append("trainee_ids", selectedTraineeIds.join(","));

      const res = await postMethodApiCall(
        `/v1/api/industry/attendance-by-industry/${industryId}/upload-signed-copy`,
        formData
      );

      if (res?.status === 200 && res?.data?.success) {
        navigate("/industry/dashboard");
      } else {
        alert(res?.data?.message || "Failed to upload signed copy. Please try again.");
      }
    } catch (error) {
      console.error("Error uploading signed copy:", error);
      alert("Failed to upload signed copy. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <AdminLayout>
    <div className="min-h-screen bg-gray-50/50 pb-10">
      <div className="mb-6">
        <button
          onClick={() => navigate("/industry/dashboard")}
          className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-primary bg-white border border-primary/30 rounded-lg hover:bg-primary/5 transition-colors mb-4"
        >
          <ChevronLeft size={16} />
          Back
        </button>

        <h1 className="text-2xl font-bold text-primary tracking-tight leading-tight">
          Approve Trainees — {monthName} {year}
        </h1>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-5 border-b border-gray-100 bg-green-50/50">
          <p className="text-base font-medium text-gray-800">
            These are the eligible students who have 80% attendance.
          </p>
          {eligibleStudents.length === 0 && (
            <p className="mt-2 text-sm text-red-500 font-medium">
              No students meet the 80% attendance threshold for {monthName} {year}.
            </p>
          )}
        </div>

        {eligibleStudents.length > 0 && (
          <>
            <GenericTable
              columns={columns}
              data={eligibleStudents}
              pagination={paginationConfig}
            />

            {!submitted ? (
              <div className="p-5 border-t border-gray-100">
                <label className="flex items-start gap-3 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={consentChecked}
                    onChange={(e) => setConsentChecked(e.target.checked)}
                    className="mt-0.5 h-5 w-5 rounded border-gray-300 text-primary focus:ring-primary/30 cursor-pointer"
                  />
                  <span className="text-sm text-gray-700 leading-relaxed">
                    I give consent for these students to get approved.
                  </span>
                </label>

                <div className="mt-5 flex justify-end">
                  <button
                    disabled={!consentChecked || submitting}
                    onClick={handleSubmitApproval}
                    className={`px-6 py-2.5 text-sm font-semibold rounded-lg shadow-sm transition-all ${
                      consentChecked && !submitting
                        ? "text-white bg-primary hover:bg-primary/90 cursor-pointer"
                        : "text-gray-400 bg-gray-200 cursor-not-allowed"
                    }`}
                  >
                    {submitting ? "Submitting..." : "Submit Approval"}
                  </button>
                </div>
              </div>
            ) : (
              <div className="p-5 border-t border-gray-100">
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                  <button
                    onClick={handleDownload}
                    disabled={downloading}
                    className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-primary rounded-lg hover:bg-primary/90 transition-colors shadow-sm disabled:opacity-60"
                  >
                    <Download size={16} />
                    {downloading ? "Downloading..." : "Download PDF"}
                  </button>

                  <button
                    disabled={!hasDownloaded}
                    onClick={() => fileInputRef.current?.click()}
                    className={`inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-lg shadow-sm transition-all ${
                      hasDownloaded
                        ? "text-white bg-primary hover:bg-primary/90 cursor-pointer"
                        : "text-gray-400 bg-gray-200 cursor-not-allowed"
                    }`}
                  >
                    <Upload size={16} />
                    Upload Signed Copy
                  </button>

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,application/pdf"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                </div>

                {!hasDownloaded && (
                  <p className="mt-3 text-xs text-gray-500">
                    Please download the Excel file first. The upload button will be enabled after download.
                  </p>
                )}

                {uploadedFile && (
                  <>
                    <label className="mt-4 flex items-start gap-3 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={signedSealConfirmed}
                        onChange={(e) => setSignedSealConfirmed(e.target.checked)}
                        className="mt-0.5 h-5 w-5 rounded border-gray-300 text-primary focus:ring-primary/30 cursor-pointer"
                      />
                      <span className="text-sm text-gray-700 leading-relaxed">
                        I confirm that the PDF is signed and bears the industry seal.
                      </span>
                    </label>

                    <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="flex items-center gap-3">
                        <FileText size={20} className="text-primary" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-800 truncate">
                            {uploadedFile.name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {(uploadedFile.size / 1024).toFixed(1)} KB
                          </p>
                        </div>
                        <button
                          onClick={handleFinalSubmit}
                          disabled={!signedSealConfirmed || uploading}
                          className={`inline-flex items-center gap-2 px-5 py-2 text-sm font-semibold rounded-lg shadow-sm transition-all ${
                            signedSealConfirmed && !uploading
                              ? "text-white bg-green-600 hover:bg-green-700 cursor-pointer"
                              : "text-gray-400 bg-gray-200 cursor-not-allowed"
                          }`}
                        >
                          <CheckCircle size={16} />
                          {uploading ? "Uploading..." : "Submit"}
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
    
    </AdminLayout>
  );
};

export default IndustryApproval;
